/**
 * Utilidades para cálculos de campañas
 * Fecha: 2026-01-06
 * Descripción: Funciones para calcular tiempo estimado y total de mensajes
 */

/**
 * Calcula el tiempo estimado de una campaña en segundos
 * @param {number} totalContactos - Total de contactos en la lista
 * @param {number} tiempoInicial - Tiempo inicial entre mensajes (segundos)
 * @param {number} cadaXContactos - Después de cuántos contactos cambia el intervalo
 * @param {number} nuevoTiempo - Nuevo tiempo de envío (segundos)
 * @returns {Object} - Objeto con segundos, minutos, horas, minutosRestantes y texto
 */
export const calculateEstimatedTime = (
  totalContactos,
  tiempoInicial,
  cadaXContactos,
  nuevoTiempo
) => {
  if (!totalContactos || totalContactos === 0) {
    return {
      segundos: 0,
      minutos: 0,
      horas: 0,
      minutosRestantes: 0,
      texto: "0 segundos"
    };
  }

  // Paso 1: Calcular tiempo de primeros contactos
  const primerosContactos = Math.min(cadaXContactos, totalContactos);
  // Si hay contactos restantes, usar todos los intervalos de la primera parte
  // Si no hay restantes, usar (N-1) intervalos
  const contactosRestantes = Math.max(0, totalContactos - cadaXContactos);
  const tiempoPrimeros = contactosRestantes > 0 
    ? primerosContactos * tiempoInicial
    : Math.max(0, primerosContactos - 1) * tiempoInicial;

  // Paso 2: Calcular tiempo de contactos restantes
  const tiempoRestantes = contactosRestantes > 0
    ? Math.max(0, contactosRestantes - 1) * nuevoTiempo
    : 0;

  // Paso 3: Sumar tiempos totales
  const tiempoTotal = tiempoPrimeros + tiempoRestantes; // en segundos

  // Paso 4: Convertir a formato legible
  const minutos = Math.floor(tiempoTotal / 60);
  const horas = Math.floor(minutos / 60);
  const minutosRestantes = minutos % 60;

  // Formatear texto
  let texto = "";
  if (horas > 0) {
    texto = `${horas} hora${horas > 1 ? 's' : ''} ${minutosRestantes} minuto${minutosRestantes !== 1 ? 's' : ''}`;
  } else if (minutos > 0) {
    texto = `${minutos} minuto${minutos > 1 ? 's' : ''}`;
  } else {
    texto = `${tiempoTotal} segundo${tiempoTotal !== 1 ? 's' : ''}`;
  }

  return {
    segundos: tiempoTotal,
    minutos: minutos,
    horas: horas,
    minutosRestantes: minutosRestantes,
    texto: texto
  };
};

/**
 * Formatea tiempo en formato legible
 * @param {number} segundos - Tiempo en segundos
 * @returns {string} - Tiempo formateado
 */
export const formatTime = (segundos) => {
  if (!segundos || segundos === 0) {
    return "0 segundos";
  }

  const minutos = Math.floor(segundos / 60);
  const horas = Math.floor(minutos / 60);
  const minutosRestantes = minutos % 60;

  if (horas > 0) {
    return `${horas}h ${minutosRestantes}m`;
  } else if (minutos > 0) {
    return `${minutos}m`;
  } else {
    return `${segundos}s`;
  }
};

/**
 * Calcula el total de mensajes según validación de WhatsApp
 * @param {Array} contacts - Lista de contactos
 * @param {boolean} validationEnabled - Si la validación de WhatsApp está habilitada
 * @returns {number} - Total de mensajes
 */
export const calculateTotalMessages = (contacts, validationEnabled = false) => {
  if (!contacts || !Array.isArray(contacts)) {
    return 0;
  }

  if (validationEnabled) {
    return contacts.filter(contact => contact.isWhatsappValid === true).length;
  }

  return contacts.length;
};

// ============================================================================
// ✅ NUEVO (08/01/2026): Funciones para Cálculos de Campañas de GRUPOS
// ============================================================================

/**
 * Calcula el tiempo estimado de una campaña de GRUPOS en segundos
 * @param {number} totalGrupos - Total de grupos en la lista
 * @param {number} tiempoInicial - Tiempo inicial entre mensajes (segundos)
 * @param {number} cadaXGrupos - Después de cuántos grupos cambia el intervalo
 * @param {number} nuevoTiempo - Nuevo tiempo de envío (segundos)
 * @returns {Object} - Objeto con segundos, minutos, horas, minutosRestantes, segundosRestantes y texto
 */
export const calculateGroupCampaignTime = (
  totalGrupos,
  tiempoInicial,
  cadaXGrupos,
  nuevoTiempo
) => {
  if (!totalGrupos || totalGrupos === 0) {
    return {
      segundos: 0,
      minutos: 0,
      horas: 0,
      minutosRestantes: 0,
      segundosRestantes: 0,
      texto: "0 segundos"
    };
  }

  // Paso 1: Calcular tiempo de primeros grupos
  const primerosGrupos = Math.min(cadaXGrupos, totalGrupos);
  const gruposRestantes = Math.max(0, totalGrupos - cadaXGrupos);
  
  // Si hay grupos restantes, usar todos los intervalos de la primera parte
  // Si no hay restantes, usar (N-1) intervalos
  const tiempoPrimeros = gruposRestantes > 0 
    ? primerosGrupos * tiempoInicial
    : Math.max(0, primerosGrupos - 1) * tiempoInicial;

  // Paso 2: Calcular tiempo de grupos restantes
  const tiempoRestantes = gruposRestantes > 0
    ? Math.max(0, gruposRestantes - 1) * nuevoTiempo
    : 0;

  // Paso 3: Sumar tiempos totales
  const tiempoTotal = tiempoPrimeros + tiempoRestantes; // en segundos

  // Paso 4: Convertir a formato legible
  const minutos = Math.floor(tiempoTotal / 60);
  const horas = Math.floor(minutos / 60);
  const minutosRestantes = minutos % 60;
  const segundosRestantes = tiempoTotal % 60;

  // Formatear texto
  let texto = `${tiempoTotal} segundos = `;
  if (horas > 0) {
    texto += `${horas} hora${horas > 1 ? 's' : ''} `;
  }
  if (minutosRestantes > 0) {
    texto += `${minutosRestantes} minuto${minutosRestantes !== 1 ? 's' : ''} `;
  }
  if (segundosRestantes > 0) {
    texto += `${segundosRestantes} segundo${segundosRestantes !== 1 ? 's' : ''}`;
  }

  return {
    segundos: tiempoTotal,
    minutos: minutos,
    horas: horas,
    minutosRestantes: minutosRestantes,
    segundosRestantes: segundosRestantes,
    texto: texto.trim()
  };
};

/**
 * Calcula el total de grupos para una campaña
 * @param {Array} groups - Lista de grupos seleccionados
 * @returns {number} - Total de grupos
 */
export const calculateTotalGroups = (groups) => {
  if (!groups || !Array.isArray(groups)) {
    return 0;
  }

  return groups.length;
};
